import React, { useState } from 'react';

import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import type { MoreIconProps } from 'src/components/news/MoreIcon';
import MoreIcon from 'src/components/news/MoreIcon';

import TooltipButton from 'src/components/ui/TooltipButton';

import Menu, { MenuItem } from 'src/components/ui/Menu';
import Icon from 'src/components/ui/Icon';
import Typography from 'src/components/ui/Typography';
import IconButton from 'src/components/ui/IconButton';
import Button from 'src/components/ui/Button';

import useAlerts from 'src/hooks/useAlerts';
import type { NewsPost } from 'src/types/__generated__/graphql';
import { ReasonForLess, WantToSeeMoreOrLess } from 'src/types/__generated__/graphql';

import { useCreateNewsFeedbackMutation } from 'src/hooks/__generated__/queries';

import useUser from 'src/hooks/useUser';
import stopPropagation from 'src/utils/stopPropagation';
import useAppRouter from 'src/hooks/useAppRouter';
import ReportModal from 'src/components/ui/ReportModal';
import SendNewsFeedbackModal from 'src/components/news/SendNewsFeedbackModal';
import useModalOpenState from 'src/hooks/useModalOpenState';
import { routePathNewsPost, routePathProfileInterests } from 'src/constants/router';

type IconProps = {
    newsPost: NewsPost;
    buttonProps?: Partial<MoreIconProps>;
};

const NewsPostMenu: React.FC<IconProps> = ({ newsPost, buttonProps }) => {
    const { t } = useTranslation(['common', 'news']);
    const { addAlert } = useAlerts();
    const currentUser = useUser();
    const router = useAppRouter();

    const [openFeedbackReasonModal, setOpenFeedbackReasonModal] = useState(false);

    const [createNewsFeedback] = useCreateNewsFeedbackMutation({
        onError(err) {
            addAlert({
                message: err.message,
                type: 'error',
            });
        },
        onCompleted(data) {
            addAlert({
                type: 'success',
                message: t(`news:newsPost:success${data.createNewsFeedback.wantToSeeMoreOrLess}`),
            });
        },
    });

    const handleEditMyInterests = async () => {
        await router.push(routePathProfileInterests);
    };
    const { props: reportModalProps, open: openReportModal } = useModalOpenState();

    const handleFeedback = async (reasonForLess: ReasonForLess, type: WantToSeeMoreOrLess) => {
        try {
            const newsPostId = newsPost.id;
            await createNewsFeedback({
                variables: {
                    wantToSeeMoreOrLess: type,
                    newsPostId,
                    reasonForLess,
                },
            });
            setOpenFeedbackReasonModal(false);
        } catch (error: unknown) {
            addAlert({
                type: 'error',
                message: String(error),
            });
        }
    };

    const handleFeedbackChosen = async (reasonForLess: ReasonForLess) => {
        await handleFeedback(reasonForLess, WantToSeeMoreOrLess.Less);
    };

    const isOnNewsPostDetailPage = router.asPath.includes(routePathNewsPost);

    const menu = [
        currentUser && (
            <MenuItem
                key="seeLessOfThisType"
                onClick={() => {
                    setOpenFeedbackReasonModal(true);
                }}
            >
                <Icon name="Minus" size="1rem" />
                <Box ml={1}>
                    <Typography variant="h6">{t('news:newsPost.seeLessOfThisType')}</Typography>
                </Box>
            </MenuItem>
        ),
        currentUser && (
            <MenuItem
                key="seeMoreOfThisType"
                onClick={() => handleFeedback(ReasonForLess.Default, WantToSeeMoreOrLess.More)}
            >
                <Icon name="Add" size="1rem" />
                <Box ml={1}>
                    <Typography variant="h6">{t('news:newsPost.seeMoreOfThisType')}</Typography>
                </Box>
            </MenuItem>
        ),
        currentUser && (
            <MenuItem key="editMyInterests" onClick={handleEditMyInterests}>
                <Icon name="Edit" size="1rem" />
                <Box ml={1}>
                    <Typography variant="h6">{t('news:newsPost.editMyInterests')}</Typography>
                </Box>
            </MenuItem>
        ),
        currentUser && (
            <MenuItem key="openReportModal" onClick={openReportModal}>
                <Icon name="Alert" size="1rem" />
                <Box ml={1}>
                    <Typography variant="h6">{t('news:newsPost.report')}</Typography>
                </Box>
            </MenuItem>
        ),
    ];

    return (
        <Box display="flex" flexDirection="row" alignItems="center" onClick={stopPropagation}>
            <Menu id={`news-post-${newsPost.id}`} items={menu}>
                <TooltipButton
                    title={t('common:tooltips.plus')}
                    ButtonComponent={MoreIcon}
                    buttonProps={{
                        ariaLabel: 'settings',
                        ariaControls: `news-post-${newsPost.id}`,
                        ariaHasPopup: 'true',
                        ...buttonProps,
                    }}
                />
            </Menu>
            {openFeedbackReasonModal && (
                <SendNewsFeedbackModal
                    open={openFeedbackReasonModal}
                    onClose={() => {
                        setOpenFeedbackReasonModal(false);
                    }}
                    onFeedbackChosen={async (feedback: ReasonForLess) => {
                        await handleFeedbackChosen(feedback);
                    }}
                />
            )}
            {reportModalProps.open && (
                <ReportModal
                    newsPostId={newsPost.id}
                    open={reportModalProps.open}
                    onClose={() => {
                        reportModalProps.onClose();
                    }}
                />
            )}
        </Box>
    );
};

export default NewsPostMenu;
